import { CssEvals } from "@/components/Canvas/Storyboard/StaticIframe/hooks/useExtractCss";
import { ICONS } from "@/components/Core/Icons";
import { CssFieldValue } from "@/components/RightStyle";

// TODO: Convert to Enum
const SUPPORTED_TYPES = [
  "BUTTON_OPTIONS",
  "SIZE_INPUT",
  "MULTI_SIZE_INPUT",
  "SELECT_FROM_OPTIONS",
  "COLOR_PICKER",
];

export interface ButtonOption {
  icon: ICONS;
  value: string;
}

export interface SizeValue {
  value: string; // Use string to allow empty values as an empty string
  unit: string;
}

export enum EXPANDED {
  NONE,
  HALF,
  FULL,
}

export interface MultiSizeValues {
  expandedType: EXPANDED;

  left?: SizeValue;
  top?: SizeValue;
  right?: SizeValue;
  bottom?: SizeValue;

  vertical?: SizeValue;
  horizontal?: SizeValue;

  all?: SizeValue;
}

export interface SelectOption {
  value: string;
  label: string;
  className?: string;
}

export const extractSizeValue = (sizeValue: any): SizeValue => {
  if (
    sizeValue === undefined ||
    sizeValue === null ||
    sizeValue === "normal" ||
    sizeValue == ""
  ) {
    return {
      value: "0",
      unit: "px",
    };
  }

  if (typeof sizeValue === "number") {
    return {
      value: sizeValue.toString(),
      unit: "px",
    };
  }

  try {
    // TODO: Improve this
    const number = sizeValue.replace(/[^\d\.]/g, "");
    if (!number || number == "") {
      return {
        value: "0",
        unit: "px",
      };
    }

    const unit = sizeValue.replace(number, "").replace(" ", "");
    return {
      value: number,
      unit: unit,
    };
  } catch {
    return {
      value: "0",
      unit: "px",
    };
  }
};

export const extractMultiSizeValue = (
  sizeValue: string,
  corners?: boolean,
): MultiSizeValues => {
  const defaultSize = {
    unit: "px",
    value: "0",
  };
  if (!sizeValue) {
    return {
      expandedType: EXPANDED.NONE,

      all: defaultSize,

      vertical: defaultSize,
      horizontal: defaultSize,

      left: defaultSize,
      right: defaultSize,
      bottom: defaultSize,
      top: defaultSize,
    };
  }

  try {
    // To Do: what if there are multiple spaces
    const separatedValues = sizeValue.split(/[ ]+/);
    if (separatedValues.length === 1) {
      const sizeValue = extractSizeValue(separatedValues[0]);
      return {
        expandedType: EXPANDED.NONE,

        all: sizeValue,
        vertical: sizeValue,
        horizontal: sizeValue,

        left: sizeValue,
        right: sizeValue,
        bottom: sizeValue,
        top: sizeValue,
      };
    } else if (separatedValues.length === 2) {
      const firstSize = extractSizeValue(separatedValues[corners ? 1 : 0]);
      const secondSize = extractSizeValue(separatedValues[corners ? 0 : 1]);

      return {
        expandedType: EXPANDED.HALF,

        // @ts-expect-error TS2322
        all: null,

        vertical: firstSize,
        horizontal: secondSize,

        left: secondSize,
        right: secondSize,
        bottom: firstSize,
        top: firstSize,
      };
    } else if (separatedValues.length === 3) {
      const firstSize = extractSizeValue(separatedValues[0]);
      const secondSize = extractSizeValue(separatedValues[1]);
      const thirdSize = extractSizeValue(separatedValues[2]);

      return {
        expandedType: EXPANDED.FULL,

        // @ts-expect-error TS2322
        all: null,

        // @ts-expect-error TS2322
        vertical: corners ? secondSize : null,
        // @ts-expect-error TS2322
        horizontal: corners ? null : secondSize,

        left: corners ? thirdSize : secondSize,
        right: corners ? firstSize : secondSize,
        bottom: corners ? secondSize : thirdSize,
        top: corners ? secondSize : firstSize,
      };
    } else if (separatedValues.length === 4) {
      // The left/right/bottom/top are based on the padding expanded designs
      // Manual work is done to map the correct corners to the right values
      const firstSize = extractSizeValue(separatedValues[corners ? 1 : 0]);
      const secondSize = extractSizeValue(separatedValues[corners ? 3 : 1]);
      const thirdSize = extractSizeValue(separatedValues[corners ? 2 : 2]);
      const fourthSize = extractSizeValue(separatedValues[corners ? 0 : 3]);

      return {
        expandedType: EXPANDED.FULL,

        // @ts-expect-error TS2322
        all: null,

        // @ts-expect-error TS2322
        vertical: null,
        horizontal: secondSize,

        left: fourthSize,
        right: secondSize,
        bottom: thirdSize,
        top: firstSize,
      };
    } else {
      return {
        expandedType: EXPANDED.NONE,

        all: defaultSize,

        vertical: defaultSize,
        horizontal: defaultSize,

        left: defaultSize,
        right: defaultSize,
        bottom: defaultSize,
        top: defaultSize,
      };
    }
  } catch {
    return {
      expandedType: EXPANDED.NONE,

      all: defaultSize,

      vertical: defaultSize,
      horizontal: defaultSize,

      left: defaultSize,
      right: defaultSize,
      bottom: defaultSize,
      top: defaultSize,
    };
  }
};

/**
 * Takes any vertical/horizontal or top/left/bottom/right styling and converts it to a single line
 *
 * If value in codebase is passed in, take into account how it's split up there
 */
export const convertExpandedDefinedStyles = (
  baseCssName: string,

  // One of these two must be passed in
  definedStylesDict?: { [cssName: string]: CssFieldValue },
  cssEvalsDict?: CssEvals | null,

  valueInCodebase?: string,
): string | null => {
  const regularSplitIntoFour =
    valueInCodebase && valueInCodebase.split(" ").length === 4;
  const regularSplitIntoTwo =
    valueInCodebase && valueInCodebase.split(" ").length === 2;

  let totalValue,
    verticalStyling,
    horizontalStyling,
    topStyling,
    rightStyling,
    bottomStyling,
    leftStyling;
  if (definedStylesDict) {
    totalValue = definedStylesDict?.[baseCssName]?.evaluatedValue;

    verticalStyling =
      definedStylesDict?.[`${baseCssName}-vertical`]?.evaluatedValue;
    horizontalStyling =
      definedStylesDict?.[`${baseCssName}-horizontal`]?.evaluatedValue;

    topStyling = definedStylesDict?.[`${baseCssName}-top`]?.evaluatedValue;
    rightStyling = definedStylesDict?.[`${baseCssName}-right`]?.evaluatedValue;
    bottomStyling =
      definedStylesDict?.[`${baseCssName}-bottom`]?.evaluatedValue;
    leftStyling = definedStylesDict?.[`${baseCssName}-left`]?.evaluatedValue;
  } else {
    totalValue = cssEvalsDict?.[baseCssName];

    verticalStyling = cssEvalsDict?.[`${baseCssName}-vertical`];
    horizontalStyling = cssEvalsDict?.[`${baseCssName}-horizontal`];

    topStyling = cssEvalsDict?.[`${baseCssName}-top`];
    rightStyling = cssEvalsDict?.[`${baseCssName}-right`];
    bottomStyling = cssEvalsDict?.[`${baseCssName}-bottom`];
    leftStyling = cssEvalsDict?.[`${baseCssName}-left`];
  }

  // Merge top and bottom if they are the same
  if (topStyling && topStyling === bottomStyling) {
    verticalStyling = topStyling;
    topStyling = null;
    bottomStyling = null;
  }

  // Merge right and left if they are the same
  if (rightStyling && rightStyling === leftStyling) {
    horizontalStyling = rightStyling;
    rightStyling = null;
    leftStyling = null;
  }

  // Return just the one value if vertical & horizontal are the same
  if (verticalStyling && verticalStyling === horizontalStyling) {
    totalValue = verticalStyling;
    verticalStyling = null;
    horizontalStyling = null;
  }

  // If any single one of these is still defined, it means we need to use all 4
  if (topStyling || rightStyling || bottomStyling || leftStyling) {
    return `${topStyling || verticalStyling || "0px"} ${
      rightStyling || horizontalStyling || "0px"
    } ${bottomStyling || verticalStyling || "0px"} ${
      leftStyling || horizontalStyling || "0px"
    }`;
  }

  // If either of these are still defined, we need to use the 2 values
  if (verticalStyling || horizontalStyling) {
    // If the regular defined style is equal to the vertical & horizontal, but split up, force the split up
    if (regularSplitIntoFour) {
      return `${verticalStyling || "0px"} ${horizontalStyling || "0px"} ${
        verticalStyling || "0px"
      } ${horizontalStyling || "0px"}`;
    }

    return `${verticalStyling || "0px"} ${horizontalStyling || "0px"}`;
  }

  if (totalValue) {
    // Return the value matching the split in the codebase
    if (regularSplitIntoTwo && totalValue.split(" ").length === 1) {
      return `${totalValue} ${totalValue}`;
    }

    if (regularSplitIntoFour) {
      if (totalValue.split(" ").length === 1) {
        return `${totalValue} ${totalValue} ${totalValue} ${totalValue}`;
      } else if (totalValue.split(" ").length === 2) {
        return `${totalValue} ${totalValue}`;
      }
    }

    return totalValue;
  }

  return null;
};

export const sizesEqual = (size1: SizeValue, size2: SizeValue): boolean => {
  return size1.value === size2.value && size1.unit === size2.unit;
};
